import _ from 'lodash'
import * as THREE from 'three'

import MathsUtils from '@/utils/mathsUtils'
import Rectangle from '@/model/rectangle'
import GlassSegment from '@/model/glassSegment'

import OptimiserNode from '@/apps/glass-production-station/src/classes/optimiserNode'
import OptimiserAllocation from '@/apps/glass-production-station/src/classes/optimiserAllocation'

import { OptimiserSheetNodeTypes, SheetAllocationTypes, OptimiserNodeSplitDirTypes, StockGrainDirectionTypes, StockFaceTypes } from '@/store/types'
import { EdgeTypes, AxisTypes, SegmentTypes } from '@/store/mathTypes'
import { Vector2 } from 'three'


/**
 * Optimiser Sheet
 * Represents the sheet allocations & node tree
 */
export default class OptimiserSheet {
  constructor(sheetData, rootNode) {

    if(sheetData) {
      this.id = sheetData.id
      this.type = sheetData.type
      this.status = sheetData.status
      this.width = sheetData.width
      this.height = sheetData.height
      this.isRotated = sheetData.isRotated
      this.sourceWidth = sheetData.sourceWidth
      this.sourceHeight = sheetData.sourceHeight
      this.stockId = sheetData.stockId
      this.isStockUsingGrain = !!sheetData.isStockUsingGrain
      this.isStockUsingFaces = !!sheetData.isStockUsingFaces
      this.defaultFaceSideUp = sheetData.defaultFaceSideUp || StockFaceTypes.smooth
      this.grainDirection = sheetData.grainDirection
      this.trimTop = sheetData.trimTop || 0
      this.trimRight = sheetData.trimRight || 0
      this.trimBottom = sheetData.trimBottom || 0
      this.trimLeft = sheetData.trimLeft || 0

      this.optimiserSheetAllocations = []
      sheetData.optimiserSheetAllocations.forEach(allocData => {
        this.optimiserSheetAllocations.push(new OptimiserAllocation(allocData))
      })

      this.nextAllocId = this._calculateNextAllocId()
      this.rootNode = this._copyNodeTree(rootNode)
      this.nextNodeId = this._calculateNextNodeId(this.rootNode)
    }
  }


  /**
   * Clone a copy of the optimiser sheet
   */
  clone() {
    let clonedSheet = new OptimiserSheet({
      id: this.id,
      type: this.type,
      status: this.status,
      width: this.width,
      height: this.height,
      isRotated: this.isRotated,
      sourceWidth: this.sourceWidth,
      sourceHeight: this.sourceHeight,
      stockId: this.stockId,
      isStockUsingGrain: this.isStockUsingGrain,
      isStockUsingFaces: this.isStockUsingFaces,
      defaultFaceSideUp: this.defaultFaceSideUp,
      grainDirection: this.grainDirection,
      trimTop: this.trimTop,
      trimRight: this.trimRight,
      trimBottom: this.trimBottom,
      trimLeft: this.trimLeft,
      optimiserSheetAllocations: this.optimiserSheetAllocations
    }, this.rootNode)

    return clonedSheet
  }


  /**
   * Copy another sheet into this sheet
   */
  copy(otherSheet) {
    this.id = otherSheet.id
    this.type = otherSheet.type
    this.status = otherSheet.status
    this.width = otherSheet.width
    this.height = otherSheet.height
    this.isRotated = otherSheet.isRotated
    this.sourceWidth = otherSheet.sourceWidth
    this.sourceHeight = otherSheet.sourceHeight
    this.stockId = otherSheet.stockId
    this.isStockUsingGrain = otherSheet.isStockUsingGrain
    this.isStockUsingFaces = otherSheet.isStockUsingFaces
    this.defaultFaceSideUp = otherSheet.defaultFaceSideUp
    this.grainDirection = otherSheet.grainDirection
    this.trimTop = otherSheet.trimTop
    this.trimRight = otherSheet.trimRight
    this.trimBottom = otherSheet.trimBottom
    this.trimLeft = otherSheet.trimLeft

    this.optimiserSheetAllocations = otherSheet.optimiserSheetAllocations.map(alloc => alloc.clone())

    this.nextAllocId = this._calculateNextAllocId()
    this.rootNode = this._copyNodeTree(otherSheet.rootNode)
    this.nextNodeId = this._calculateNextNodeId(this.rootNode)
  }


  /**
   * Rotate the sheet. Toggles between 0 & 90 degrees
   * Rotates all allocations & nodes
   */
  rotateSheet() {
    let prevRotation = this.isRotated
    let prevWidth = this.width
    let prevHeight = this.height

    this.isRotated = !this.isRotated
    this.width = this.isRotated ? this.sourceHeight : this.sourceWidth
    this.height = this.isRotated ? this.sourceWidth : this.sourceHeight

    // Rotate allocations
    this.optimiserSheetAllocations.forEach(alloc => {
      let prevAllocY = alloc.y
      let prevAllocWidth = alloc.width
      let prevAllocHeight = alloc.height

      if(this.isRotated) {
        alloc.y = alloc.x
        alloc.x = prevHeight - (prevAllocY + prevAllocHeight)
      } else {
        alloc.y = prevWidth - (alloc.x + prevAllocWidth)
        alloc.x = prevAllocY
      }

      alloc.height = alloc.width
      alloc.width = prevAllocHeight
      alloc.isRotated = !alloc.isRotated
      alloc.angle += (this.isRotated ? 90 : -90)
      alloc.angle = MathsUtils.wrapAngleDegrees(alloc.angle)
    })

    // Rotate node tree
    this._rotateNode(this.rootNode, this.isRotated, prevWidth, prevHeight)
  }


  /**
   * Rotate a node in the tree. Done when the sheet is rotated between 0 & 90 degrees
   * Works recursively for this node and down the tree
   */
  _rotateNode(node, rotateFoward, prevSheetWidth, prevSheetHeight) {
    if(!node)
      return

    node.angle += (rotateFoward ? 90 : -90)
    node.angle = MathsUtils.wrapAngleDegrees(node.angle)

    let prevNodeY = node.y
    let prevNodeWidth = node.width
    let prevNodeHeight = node.height

    if(rotateFoward) {
      node.y = node.x
      node.x = prevSheetHeight - (prevNodeY + prevNodeHeight)
    } else {
      node.y = prevSheetWidth - (node.x + prevNodeWidth)
      node.x = prevNodeY
    }

    node.height = node.width
    node.width = prevNodeHeight

    this._rotateNode(node.childNodes[0], rotateFoward, prevSheetWidth, prevSheetHeight)
    this._rotateNode(node.childNodes[1], rotateFoward, prevSheetWidth, prevSheetHeight)
  }


  /**
   * Clear the node (and remove allocation links) and remove children
   * Works recursively for this node and down the tree
   * 
   * Returns a list of allocations that were removed
   */
  _clearNode(node, removeTypes = null, removeAllocations = false) {
    if(!node)
      return []

    // If node type is no in remove types, ignore it & don't continue
    // Ignore if remove types is null
    if(removeTypes && removeTypes.indexOf(node.type) == -1)
      return []

    let clearedAllocList = []

    if(removeAllocations) {
      // Remove any allocations linked to this node
      clearedAllocList = this.optimiserSheetAllocations.filter(alloc => alloc.packingNodeId == node.id)

      this.optimiserSheetAllocations = this.optimiserSheetAllocations.filter(alloc => {
        return alloc.packingNodeId != node.id
      })
    }

    node.panelId = null
    node.type = OptimiserSheetNodeTypes.waste

    // Clear children
    clearedAllocList = _.concat(clearedAllocList, this._clearNode(node.childNodes[0], removeTypes, removeAllocations))
    clearedAllocList = _.concat(clearedAllocList, this._clearNode(node.childNodes[1], removeTypes, removeAllocations))
    
    node.childNodes[0] = null
    node.childNodes[1] = null

    return clearedAllocList
  }


  /**
   * Copy the optimiser sheet node
   * Recursives down to child nodes & so on
   */
  _copyNodeTree(node, parentNode = null) {
    if(node == null)
      return null

    let newNode = new OptimiserNode(parentNode, node)

    newNode.childNodes[0] = this._copyNodeTree(node.childNodes[0], newNode)
    newNode.childNodes[1] = this._copyNodeTree(node.childNodes[1], newNode)

    return newNode
  }


  /**
   * Find a node by an ID
   * Recursives through the tree to look for the node
   * 
   * Returns the OptimiserSheetNode object if found
   * Returns null if not found
   */
  findNodeById(id, node = null) {
    if(node == null)
      node = this.rootNode

    if(node.id == id)
      return node

    if(node.childNodes[0]) {
      let foundNode = this.findNodeById(id, node.childNodes[0])
      if(foundNode)
        return foundNode
    }
    if(node.childNodes[1]) {
      let foundNode = this.findNodeById(id, node.childNodes[1])
      if(foundNode)
        return foundNode
    }

    return null
  }


  /**
   * Flatten the node tree from a node down into a single array
   */
  _flattenNodeDown(node) {
    if(node == null)
      return []

    return _.concat([node], this._flattenNodeDown(node.childNodes[0]), this._flattenNodeDown(node.childNodes[1]))
  }


  /**
   * Flatten the node tree from a node up into a single array
   */
   _flattenNodeUp(node) {
    if(node == null)
      return []

    return _.concat([node], this._flattenNodeUp(node.parentNode))
  }


  /**
   * Runs a cleanup on the nodes in the tree
   * Any nodes that have empty children are cleaned up and children removed
   */
  _cleanupNodes(node) {
    if(!node)
      return false

    let validLockedAllocs = this.optimiserSheetAllocations.filter(alloc => {
      if(alloc.type == SheetAllocationTypes.waste)
        return alloc.isUserLocked
      return true
    })

    let nodeContainsAllocs = this._nodeLeafsContainsAllocs(node, validLockedAllocs)

    if(!nodeContainsAllocs) {
      // Clear node
      this._clearNode(node, null, false)
    } else {
      // Check children
      this._cleanupNodes(node.childNodes[0])
      this._cleanupNodes(node.childNodes[1])
    }
  }


  /**
   * Find a common parent node given a list of nodes
   */
  _calculateCommonParentForNodeList(nodeList) {
    if(nodeList.length == 0)
      return null
    if(nodeList.length == 1)
      return nodeList[0].parentNode

    // Compare against all other nodes
    let parentIdLists = nodeList.map(node => {
      return _.compact(this._flattenNodeUp(node).map(node => node.parentNode ? node.parentNode.id : null))
    })

    // Find intersections
    let intersections = _.intersection(...parentIdLists)
    if(intersections.length > 0)
      return this.findNodeById(intersections[0])

    return null
  }


  /**
   * Check if a node or leafs at the end contain any allocations in the list
   */
  _nodeLeafsContainsAllocs(node, lockedAllocsList) {
    if(!node)
      return false

    if(node.type == OptimiserSheetNodeTypes.panel) {
      let foundAlloc = lockedAllocsList.find(alloc => {
        if(alloc.type == SheetAllocationTypes.panel)
          return (alloc.productionPanelId == node.panelId)
        if(alloc.type == SheetAllocationTypes.blankPanel)
          return (alloc.blankPanelUUID == node.panelId)
        return false
      })

      if(foundAlloc)
        return true
    }
    if(node.type == OptimiserSheetNodeTypes.defect) {
      let foundAlloc = lockedAllocsList.find(alloc => {
        if(alloc.type == SheetAllocationTypes.defect)
          return (alloc.packingNodeId == node.id)
        return false
      })

      if(foundAlloc)
        return true
    }
    if(node.type == OptimiserSheetNodeTypes.offcut) {
      let foundAlloc = lockedAllocsList.find(alloc => {
        if(alloc.type == SheetAllocationTypes.offcut)
          return (alloc.packingNodeId == node.id)
        return false
      })

      if(foundAlloc)
        return true
    }
    if(node.type == OptimiserSheetNodeTypes.waste) {
      let foundAlloc = lockedAllocsList.find(alloc => {
        if(alloc.type == SheetAllocationTypes.waste)
          return (alloc.packingNodeId == node.id)
        return false
      })

      if(foundAlloc)
        return true
    }

    // Go down children
    if(node.childNodes && node.childNodes.length == 2) {
      if(this._nodeLeafsContainsAllocs(node.childNodes[0], lockedAllocsList))
        return true
      if(this._nodeLeafsContainsAllocs(node.childNodes[1], lockedAllocsList))
        return true
    }

    return false
  }


  /**
   * Calculate the next available ID for nodes in a tree
   */
  _calculateNextNodeId(node) {
    if(node == null)
      return 0
    
    let nextNodeId = node.id + 1

    nextNodeId = Math.max(nextNodeId, this._calculateNextNodeId(node.childNodes[0]))
    nextNodeId = Math.max(nextNodeId, this._calculateNextNodeId(node.childNodes[1]))

    return nextNodeId
  }


  /**
   * Calculate the next available ID for allocations in the sheet
   */
  _calculateNextAllocId() {
    let nextAllocId = 1

    this.optimiserSheetAllocations.forEach(alloc => {
      nextAllocId = Math.max(nextAllocId, alloc.id + 1)
    })

    return nextAllocId
  }


  /**
   * Attempt to insert a new panel into the tree
   * Does not modify the existing node tree structure, only attempts to create a new allocation/nodes for the panel if it fits in the target region
   * 
   * If successful, will update the tree structure & return the new allocation
   * If the panel cannot be inserted, will return null and leave the tree structure unchanged
   */
  insertPanelInTree(panelId, targetLeft, targetTop, targetWidth, targetHeight, targetAngle, targetFlipped, targetPosition) {
    if(targetWidth > this.width || targetHeight > this.height)
      return null

    // Don't go over boundaries
    targetLeft = Math.max(Math.min(this.width - targetWidth, targetLeft), 0)
    targetTop = Math.max(Math.min(this.height - targetHeight, targetTop), 0)

    targetPosition.x = Math.max(Math.min(this.width, targetPosition.x), 0)
    targetPosition.y = Math.max(Math.min(this.height, targetPosition.y), 0)
    targetPosition.x = Math.round(targetPosition.x)
    targetPosition.y = Math.round(targetPosition.y)

    // Run generic tree shaking cleanup that removes empty children from nodes
    this._cleanupNodes(this.rootNode)

    // Create a new allocation
    // This is just temporary allocation
    // The server will be updated & return the newly created allocation object that matches the database record
    let newAlloc = new OptimiserAllocation({
      id: this.nextAllocId++,
      type: SheetAllocationTypes.panel,
      productionPanelId: panelId,
      angle: targetAngle,
      flippedHorz: targetFlipped,
      flippedVert: false,
      x: targetLeft,
      y: targetTop,
      width: targetWidth,
      height: targetHeight,
      sourceWidth: targetWidth,
      sourceHeight: targetHeight
    })

    let availNode = this.findNodeAtPosition(targetPosition.x, targetPosition.y)
    if(availNode) {
      // Remove offcuts, waste & other nodes
      // Does NOT remove existing panels
      this._clearNode(availNode, [OptimiserSheetNodeTypes.node, OptimiserSheetNodeTypes.offcut, OptimiserSheetNodeTypes.waste], true)


      // Snap to the node if it's big enough
      if(availNode.width >= newAlloc.width && availNode.height >= newAlloc.height) {
        let splitTop = (newAlloc.y - availNode.y) < ((availNode.y + availNode.height) - (newAlloc.y+newAlloc.height))
        let splitLeft = (newAlloc.x - availNode.x) < ((availNode.x + availNode.width) - (newAlloc.x+newAlloc.width))

        if(splitTop)
          newAlloc.y = availNode.y
        else
          newAlloc.y = availNode.y + availNode.height - newAlloc.height
        if(splitLeft)
          newAlloc.x = availNode.x
        else
          newAlloc.x = availNode.x + availNode.width - newAlloc.width
      } else {
        availNode = null
      }
    }

    if(availNode) {
      let resultNode = this._insertAllocationToNode(newAlloc, true, availNode, newAlloc.width, newAlloc.height, newAlloc.angle, [], targetPosition)
      if(resultNode) {
        // Back into list
        this.optimiserSheetAllocations.push(newAlloc)
        return newAlloc
      }      
    }

    return null
  }


  /**
   * Insert a new panel straight to the scratch pad area
   * Does not modify the node structure, just adds a new allocation for scratch pad
   * 
   */
  insertPanelToScratchPad(panelId, targetLeft, targetTop, targetWidth, targetHeight, targetAngle) {
    // Create a new scratch pad allocation
    // This is just temporary allocation
    // The server will be updated & return the newly created allocation object that matches the database record
    let newAlloc = new OptimiserAllocation({
      id: this.nextAllocId++,
      type: SheetAllocationTypes.panelScratchPad,
      productionPanelId: panelId,
      angle: targetAngle,
      flippedHorz: false,
      flippedVert: false,
      x: targetLeft,
      y: targetTop,
      width: targetWidth,
      height: targetHeight,
      sourceWidth: targetWidth,
      sourceHeight: targetHeight
    })

    this.optimiserSheetAllocations.push(newAlloc)
  }


  /**
   * Attempt to resize / move a node in the tree to a target rectangle
   * Moves up the tree removing/resizing nodes to adjust for the target rectangle
   * Will not adjust the nodes if they contain locked allocations
   * 
   * If successful, will update the tree structure & return true
   * If the node cannot be changed, will return false and leave the tree structure unchanged
   */
  resizeNodePositionInTree(srcAlloc, srcNodeId, targetLeft, targetTop, targetWidth, targetHeight, targetAngle, targetPosition, options = {}) {
    if(targetWidth > this.width || targetHeight > this.height)
      return false

    // Don't go over boundaries
    targetLeft = Math.max(Math.min(this.width - targetWidth, targetLeft), 0)
    targetTop = Math.max(Math.min(this.height - targetHeight, targetTop), 0)

    targetPosition.x = Math.max(Math.min(this.width, targetPosition.x), 0)
    targetPosition.y = Math.max(Math.min(this.height, targetPosition.y), 0)
    targetPosition.x = Math.round(targetPosition.x)
    targetPosition.y = Math.round(targetPosition.y)

    // If the node was resized (rotated, size overridden, trim added, etc) attempt to find a better top/left position
    // The node can be anchored at any of the corners and resize in the opposite direction
    if(options.nodeResized && options.oldAllocRect) {
      let diffW = targetWidth - options.oldAllocRect.getWidth()
      let diffH = targetHeight - options.oldAllocRect.getHeight()
      if(diffW > 0 || diffH > 0) {
        let targetList = [
          new Rectangle(targetLeft, targetTop, targetWidth, targetHeight),    // Original requested position
          new Rectangle(targetLeft, targetTop - diffH, targetWidth, targetHeight),
          new Rectangle(targetLeft - diffW, targetTop, targetWidth, targetHeight),
          new Rectangle(targetLeft - diffW, targetTop - diffH, targetWidth, targetHeight),
          new Rectangle(targetLeft + diffW, targetTop - diffH, targetWidth, targetHeight),
          new Rectangle(targetLeft + diffW, targetTop + diffH, targetWidth, targetHeight),
          new Rectangle(targetLeft - diffW, targetTop + diffH, targetWidth, targetHeight)
        ]
        // Attempt all possible solutions & see if it intersects with anything
        // Only consider panels / defects
        let foundValidPosition = false
        for(var targetSln of targetList) {
          // Shrink by 1 to avoid intersectings when resting against each other
          targetSln.growRectangle(-1)

          // If outside of the sheet, ignore it
          if(targetSln.getLeft() < this.trimLeft || targetSln.getTop() < this.trimTop)
            continue
          if(targetSln.getRight() >= this.width-this.trimRight || targetSln.getBottom() >= this.height-this.trimBottom)
            continue

          let allocIntersecting = this.findSheetAllocationsIntersectingRectangle(targetSln, { ignoreAllocId: srcAlloc.id, types: [SheetAllocationTypes.panel, SheetAllocationTypes.defect, SheetAllocationTypes.blankPanel] })

          // If no intersections, use this
          if(allocIntersecting.length == 0) {
            targetLeft = targetSln.getLeft()
            targetTop = targetSln.getTop()
            targetPosition.x = targetLeft + targetWidth/2
            targetPosition.y = targetTop + targetHeight/2
            foundValidPosition = true

            break
          }
        }

        // If we didn't find a valid position, attempt to move other allocations in the most appropriate direction based on this allocation's position in the sheet
        // Get all allocs in the direction we should resize, sort by furthest away, then move out of the way & try again
        // This isn't going to work in all cases, but it may help in cases with simpler sheets & larger panels
        // This fails with complex sheets with lots of small panels
        if(!foundValidPosition && options.moveOtherAllocs) {
          let allocSheetTargetRect = new Rectangle(targetLeft, targetTop, targetWidth, targetHeight)
          var allocIsLeft = allocSheetTargetRect.getLeft() < (this.width - allocSheetTargetRect.getRight())
          var allocIsTop = allocSheetTargetRect.getTop() < (this.height - allocSheetTargetRect.getBottom())

          var allocTestHorz = allocSheetTargetRect.clone()
          var allocTestVert = allocSheetTargetRect.clone()

          if(allocIsLeft)
            allocTestHorz.expandByPoint(this.width, allocTestHorz.getTop())
          else
            allocTestHorz.expandByPoint(0, allocTestHorz.getTop())

          if(allocIsTop)
            allocTestVert.expandByPoint(allocTestVert.getLeft(), this.height)
          else
            allocTestVert.expandByPoint(allocTestVert.getLeft(), 0)

          // Shrink by 1 to avoid getting neighbours right up against the edge
          allocTestHorz.growRectangle(-1)
          allocTestVert.growRectangle(-1)

          // Move all them & retry this resize function
          if(diffW > 0) {
            let horzAllocs = this.findSheetAllocationsIntersectingRectangle(allocTestHorz, { ignoreAllocId: srcAlloc.id, types: [SheetAllocationTypes.panel, SheetAllocationTypes.defect, SheetAllocationTypes.blankPanel] })
            horzAllocs = this.sortAllocationsInDirection(horzAllocs, new THREE.Vector2(allocIsLeft ? -1 : 1, 0))

            // Move left/right
            let horzPosition = 0
            for(let movingAlloc of horzAllocs) {
              let movingPos = movingAlloc.getRectangle().getCenter()
              var horzOffset = 0

              if(allocIsLeft) {
                horzOffset = (this.width - movingAlloc.x - movingAlloc.width) - horzPosition
              } else {
                horzOffset = -movingAlloc.x + horzPosition
              }
              horzPosition += movingAlloc.width

              movingPos.x += horzOffset
              if(!this.resizeNodePositionInTree(movingAlloc, movingAlloc.packingNodeId, movingAlloc.x + horzOffset, movingAlloc.y, movingAlloc.width, movingAlloc.height, movingAlloc.angle, movingPos))
                return false
            }
          }

          if(diffH > 0) {
            let vertAllocs = this.findSheetAllocationsIntersectingRectangle(allocTestVert, { ignoreAllocId: srcAlloc.id, types: [SheetAllocationTypes.panel, SheetAllocationTypes.defect, SheetAllocationTypes.blankPanel] })
            vertAllocs = this.sortAllocationsInDirection(vertAllocs, new THREE.Vector2(0, allocIsTop ? -1 : 1))

            // Move up/down
            let vertPosition = 0
            for(let movingAlloc of vertAllocs) {
              let movingPos = movingAlloc.getRectangle().getCenter()
              var vertOffset = 0

              if(allocIsTop) {
                vertOffset = (this.height - movingAlloc.y - movingAlloc.height) - vertPosition
              } else {
                vertOffset = -movingAlloc.y + vertPosition
              }
              vertPosition += movingAlloc.height

              movingPos.y += vertOffset
              if(!this.resizeNodePositionInTree(movingAlloc, movingAlloc.packingNodeId, movingAlloc.x, movingAlloc.y + vertOffset, movingAlloc.width, movingAlloc.height, movingAlloc.angle, movingPos))
                return false
            }
          }

          // Try again
          return this.resizeNodePositionInTree(srcAlloc, srcAlloc.packingNodeId, targetLeft, targetTop, targetWidth, targetHeight, targetAngle, targetPosition, { nodeResized: true, oldAllocRect: options.oldAllocRect, moveOtherAllocs: false })
        }
      }
    }

    let removedAllocs = []

    if(srcNodeId) {
      // Copy the node structure to modify
      let rootNodeCopy = this._copyNodeTree(this.rootNode)
      let srcNodeCopy = this.findNodeById(srcNodeId, rootNodeCopy)

      if(!srcNodeCopy) {
        console.error('Could not find node copy')
        return false
      }

      let destNodeCopy = this._resizeNode(srcNodeId, srcNodeCopy, targetLeft, targetTop, targetWidth, targetHeight)
      if(destNodeCopy) {

        // Clear the destination node
        let destNode = this.findNodeById(destNodeCopy.id)
        removedAllocs = this._clearNode(destNode, null, true)

        // If the source allocation is not removed, clear that node specifically
        if(removedAllocs.find(listAlloc => listAlloc.id == srcAlloc.id) == null) {
          removedAllocs = _.concat(removedAllocs, this._clearNode(this.findNodeById(srcAlloc.packingNodeId), null, true))
        }

      } else {
        return false
      }
    } else {
      // Remove the src allocation
      this.optimiserSheetAllocations = this.optimiserSheetAllocations.filter(alloc => alloc.id != srcAlloc.id)
      removedAllocs.push(srcAlloc)

      let availNode = this.findNodeAtPosition(targetPosition.x, targetPosition.y)
      if(availNode) {
        // Remove offcuts, waste & other nodes
        // Does NOT remove existing panels
        this._clearNode(availNode, [OptimiserSheetNodeTypes.node, OptimiserSheetNodeTypes.offcut, OptimiserSheetNodeTypes.waste], true)
      }
    }
      
    // Locked allocations first, then source allocation, then the remainder
    let removedLockedAllocs = []

    // Remove non-panels / non-defects / non-blanks
    removedAllocs = removedAllocs.filter(a => a.type == SheetAllocationTypes.panel || a.type == SheetAllocationTypes.panelScratchPad || a.type == SheetAllocationTypes.defect || a.type == SheetAllocationTypes.blankPanel)

    removedLockedAllocs = _.concat(removedLockedAllocs, removedAllocs.filter(alloc => alloc.id != srcAlloc.id))
    removedLockedAllocs = _.concat(removedLockedAllocs, removedAllocs.filter(alloc => alloc.id == srcAlloc.id))

    // Check for duplicates
    removedLockedAllocs = _.uniqBy(removedLockedAllocs, 'id')

    // Insert locked first
    srcAlloc.x = targetLeft
    srcAlloc.y = targetTop

    // Run generic tree shaking cleanup that removes empty children from nodes
    this._cleanupNodes(this.rootNode)

    let sourceAllocInserted = false

    // Attempt to re-insert the remaining panels & defects that were removed
    removedLockedAllocs.forEach(alloc => {
      if(alloc.type != SheetAllocationTypes.panel && alloc.type != SheetAllocationTypes.panelScratchPad && alloc.type != SheetAllocationTypes.defect && alloc.type != SheetAllocationTypes.blankPanel)
        return

      let targetNode = this.rootNode
      let allocWidth = alloc.width
      let allocHeight = alloc.height
      let allocAngle = alloc.angle

      if(alloc.id == srcAlloc.id) {
        allocWidth = targetWidth
        allocHeight = targetHeight
        allocAngle = targetAngle
      }

      let forceAllocPosition = null
      if(alloc.id == srcAlloc.id) {
        forceAllocPosition = targetPosition

        let availNode = this.findNodeAtPosition(targetPosition.x, targetPosition.y)
        if(availNode) {

          // Snap to the node if it's big enough
          if(availNode.width >= allocWidth && availNode.height >= allocHeight) {
            let splitTop = (alloc.y - availNode.y) < ((availNode.y + availNode.height) - (alloc.y+allocHeight))
            let splitLeft = (alloc.x - availNode.x) < ((availNode.x + availNode.width) - (alloc.x+allocWidth))

            if(splitTop)
              alloc.y = availNode.y
            else
              alloc.y = availNode.y + availNode.height - allocHeight
            if(splitLeft)
              alloc.x = availNode.x
            else
              alloc.x = availNode.x + availNode.width - allocWidth

            targetNode = availNode
          }
        }
      }


      // Generate list of rectangles required in the sheet to avoid splitting into
      let requiredRects = removedLockedAllocs.filter(rmAlloc => rmAlloc.id != alloc.id).map(rmAlloc => {
        let outRect = {
          id: rmAlloc.id,
          x: rmAlloc.x,
          y: rmAlloc.y,
          width: rmAlloc.width,
          height: rmAlloc.height
        }

        if(rmAlloc.id == srcAlloc.id) {
          outRect.width = targetWidth
          outRect.height = targetHeight
        }
        return outRect
      })
      
      let forcePosition = true

      let resultNode = this._insertAllocationToNode(alloc, forcePosition, targetNode, allocWidth, allocHeight, allocAngle, requiredRects, forceAllocPosition)
      if(resultNode) {
        alloc.width = allocWidth
        alloc.height = allocHeight
        alloc.angle = allocAngle

        if(alloc.id == srcAlloc.id)
          sourceAllocInserted = true

        // Back into list
        this.optimiserSheetAllocations.push(alloc)
      }
    })

    return sourceAllocInserted
  }


  /**
   * Check if a node can be resized to allow rectangle in its place
   * Moves up the tree, breaking children until a suitable location can be found
   * If the target does fit, moves down the tree until the smallest node is found
   * Called internally from resizeNodePositionInTree
   * 
   */
  _resizeNode(srcNodeId, node, targetLeft, targetTop, targetWidth, targetHeight, traverseDownOnly = false) {

    let targetFitsNode = (testNode) => {
      let fitPosThreshold = 30    // Give some threshold to allow fitting into this node with coordinates
      let testFit = true

      if(targetWidth > testNode.width || targetHeight > testNode.height)
        testFit = false

      if(targetLeft < testNode.x - fitPosThreshold || targetTop < testNode.y - fitPosThreshold)
        testFit = false
      if(targetLeft+targetWidth > testNode.x + testNode.width + fitPosThreshold || targetTop+targetHeight > testNode.y + testNode.height + fitPosThreshold)
        testFit = false
      return testFit
    }
    
    if(targetFitsNode(node)) {
      // Get all nodes from here down & make sure no allocations are locked
      // With the exception of the node being adjusted
      let flattenedNodeList = this._flattenNodeDown(node)

      for(let childNode of flattenedNodeList) {
        if(childNode.id == srcNodeId)
          continue

        let nodeAlloc = this.findAllocationAtNodeId(childNode.id)
        if(nodeAlloc && nodeAlloc.isUserLocked) {

          // Check the target doesn't overlap a locked allocation
          if(childNode.isRectangleIntersection(targetLeft, targetTop, targetWidth, targetHeight)) {
            return null
          }
        }
      }

      // Go down tree to smallest child
      for(let childNode of node.childNodes) {
        if(!childNode)
          continue

        if(targetFitsNode(childNode)) {
          let testChildNode = this._resizeNode(srcNodeId, childNode, targetLeft, targetTop, targetWidth, targetHeight, true)
          if(testChildNode)
            return testChildNode
        }        
      }

      return node

    } else if(!traverseDownOnly) {

      // Move up tree
      if(node.parentNode) {
        return this._resizeNode(srcNodeId, node.parentNode, targetLeft, targetTop, targetWidth, targetHeight, false)
      }
    }

    return null
  }


  /**
   * Insert an allocation into a node
   * Will subdivide the node as required
   * Called internally from resizeNodePositionInTree()
   */
  _insertAllocationToNode(allocation, forcePosition, node, targetWidth, targetHeight, targetAngle, requiredRectangleList, forceAllocPosition, insertLevel = 0) {
    if(!node)
      return null
    if(targetWidth > node.width || targetHeight > node.height)
      return null

    // If recursed too far, quit out before causing an overflow
    var maxInsertLevel = 500
    if(insertLevel > maxInsertLevel)
      return null

    // If the node is used, ignore
    if(node.type != OptimiserSheetNodeTypes.node && node.type != OptimiserSheetNodeTypes.waste)
      return null

    if(forcePosition) {
      if(allocation.x < node.x || allocation.x > node.x+node.width)
        return null
      if(allocation.y < node.y || allocation.y > node.y+node.height)
        return null
    }

    if(node.childNodes[0] != null || node.childNodes[1] != null) {
      // Default to bottom-left
      let childInsertNodeIndex = 0;
      if(node.splitDir == OptimiserNodeSplitDirTypes.horizontal)
        childInsertNodeIndex = 1;

      // Attempt children
      var child1Fit = this._insertAllocationToNode(allocation, forcePosition, node.childNodes[childInsertNodeIndex], targetWidth, targetHeight, targetAngle, requiredRectangleList, forceAllocPosition, insertLevel + 1);
      if(child1Fit)
        return child1Fit;

      return this._insertAllocationToNode(allocation, forcePosition, node.childNodes[childInsertNodeIndex == 0 ? 1 : 0], targetWidth, targetHeight, targetAngle, requiredRectangleList, forceAllocPosition, insertLevel + 1);
    } else {

      // If fits exactly, place into the node
      if(targetWidth == node.width && targetHeight == node.height) {

        allocation.width = targetWidth
        allocation.height = targetHeight
        allocation.angle = targetAngle
        
        if(allocation.type == SheetAllocationTypes.panel || allocation.type == SheetAllocationTypes.panelScratchPad) {
          node.panelId = allocation.productionPanelId
          node.type = OptimiserSheetNodeTypes.panel
          allocation.type = SheetAllocationTypes.panel

          node.angle = allocation.angle
          node.flippedHorz = allocation.flippedHorz
          node.flippedVert = allocation.flippedVert
        }
        else if(allocation.type == SheetAllocationTypes.offcut) {
          node.type = OptimiserSheetNodeTypes.offcut
        }
        else if(allocation.type == SheetAllocationTypes.defect) {
          node.type = OptimiserSheetNodeTypes.defect
        }
        else if(allocation.type == SheetAllocationTypes.blankPanel) {
          node.type = OptimiserSheetNodeTypes.panel
          node.panelId = allocation.blankPanelUUID

          node.angle = allocation.angle
          node.flippedHorz = allocation.flippedHorz
          node.flippedVert = allocation.flippedVert
        }

        allocation.packingNodeId = node.id
        allocation.x = node.x
        allocation.y = node.y
        return node
      }

      // Split the node
      let remainderW = node.width - targetWidth
      let remainderH = node.height - targetHeight

      // Shorter Leftover Axis Split Rule (SLAS)
      // Default
      node.splitDir = (remainderW < remainderH) ? OptimiserNodeSplitDirTypes.horizontal : OptimiserNodeSplitDirTypes.vertical

      let insertNodeIndex = 0
      let splitTop = (allocation.y + targetHeight/2) < (node.y + node.height/2)
      let splitRight = (allocation.x + targetWidth/2) > (node.x + node.width/2)

      let horzSplitY = splitTop ? (node.y + targetHeight) : (node.y + node.height - targetHeight)
      let vertSplitX = splitRight ? (node.x + node.width - targetWidth) : (node.x + targetWidth)
      
      let horzSplitRemainder = node.height - targetHeight
      let vertSplitRemainder = node.width - targetWidth

      // If the split would intersect a required rectangle, attempt to flip the splitting direction
      // Compare intersection count between split directions & pick best result
      let horzIntCount = requiredRectangleList.filter(rect => {       
        return this._splitLineIntersectsRect(node.x, horzSplitY, node.x+node.width, horzSplitY, rect)

      }).length
      let vertIntCount = requiredRectangleList.filter(rect => {
        return this._splitLineIntersectsRect(vertSplitX, node.y, vertSplitX, node.y+node.height, rect)

      }).length
      
      if(node.splitDir == OptimiserNodeSplitDirTypes.horizontal) {

        if(vertIntCount < horzIntCount && vertSplitRemainder > 0)
          node.splitDir = OptimiserNodeSplitDirTypes.vertical
      } else {
        if(horzIntCount < vertIntCount && horzSplitRemainder > 0)
          node.splitDir = OptimiserNodeSplitDirTypes.horizontal
      }


      if(node.splitDir == OptimiserNodeSplitDirTypes.horizontal) {
        insertNodeIndex = 1;

        // Adjust split based on if allocation is closer to top or bottom of the node
        if(splitTop)
          insertNodeIndex = 0

        let splitPositionY = splitTop ? (node.y + targetHeight) : (node.y + node.height - targetHeight)

        if(forcePosition) {
          // Ensure allocation can fit inside this node
          if(allocation.y > node.y && allocation.y + targetHeight < node.y+node.height) {
            splitPositionY = allocation.y
            insertNodeIndex = 1
          }          
        }

        node.childNodes[0] = new OptimiserNode(node, {
          id: this.nextNodeId++,
          type: OptimiserSheetNodeTypes.node,
          x: node.x,
          y: node.y,
          width: node.width,
          height: splitPositionY - node.y
        })
        node.childNodes[1] = new OptimiserNode(node, {
          id: this.nextNodeId++,
          type: OptimiserSheetNodeTypes.node,
          x: node.x,
          y: splitPositionY,
          width: node.width,
          height: node.height - (splitPositionY - node.y)
        })

      } else {
        insertNodeIndex = 0;

        // Adjust split based on if allocation is closer to left or right of the node
        if(splitRight)
          insertNodeIndex = 1

        let splitPositionX = splitRight ? (node.x + node.width - targetWidth) : (node.x + targetWidth)

        if(forcePosition) {
          // Ensure allocation can fit inside this node
          if(allocation.x > node.x && allocation.x + targetWidth < node.x+node.width) {
            splitPositionX = allocation.x
            insertNodeIndex = 1
          }          
        }

        node.childNodes[0] = new OptimiserNode(node, {
          id: this.nextNodeId++,
          type: OptimiserSheetNodeTypes.node,
          x: node.x,
          y: node.y,
          width: splitPositionX - node.x,
          height: node.height
        })
        node.childNodes[1] = new OptimiserNode(node, {
          id: this.nextNodeId++,
          type: OptimiserSheetNodeTypes.node,
          x: splitPositionX,
          y: node.y,
          width: node.width - (splitPositionX - node.x),
          height: node.height
        })
      }

      return this._insertAllocationToNode(allocation, forcePosition, node.childNodes[insertNodeIndex], targetWidth, targetHeight, targetAngle, requiredRectangleList, forceAllocPosition, insertLevel + 1)
    }
  }


  /**
   * Check if a node split line intersects a rectangle
   * Can assume line will either be vertical or horizontal. Never diagonal
   * Intersections on the rectangle boundaries are ignored
   */
  _splitLineIntersectsRect(startX, startY, endX, endY, rect) {
    if(startX == endX) {
      // Vertical line
      if(startX <= rect.x || startX >= rect.x+rect.width)
        return false
      if(endY <= rect.y || startY >= rect.y+rect.height)
        return false

      return true
    } else {

      // Horizontal line
      if(startY <= rect.y || startY >= rect.y+rect.height)
        return false
      if(endX <= rect.x || startX >= rect.x+rect.width)
        return false

      return true
    }
  }


  /**
   * Subdivide a node manually
   * Assumes the node does not already have children
   */
  _subdivideNode(node, splitDirType, splitWidthHeight) {

    node.type = OptimiserSheetNodeTypes.node
    node.splitDir = splitDirType

    if(node.splitDir == OptimiserNodeSplitDirTypes.horizontal) {
      if(splitWidthHeight <= 0 || splitWidthHeight >= node.height)
        throw new Error(`Split height ${splitWidthHeight} is out of range for node height ${node.height}`)
      
      // Horizontal Split
      node.childNodes[0] = new OptimiserNode(node, {
        id: this.nextNodeId++,
        type: OptimiserSheetNodeTypes.node,
        x: node.x,
        y: node.y,
        width: node.width,
        height: splitWidthHeight
      })
      node.childNodes[1] = new OptimiserNode(node, {
        id: this.nextNodeId++,
        type: OptimiserSheetNodeTypes.node,
        x: node.x,
        y: node.y + splitWidthHeight,
        width: node.width,
        height: node.height - splitWidthHeight
      })

    } else {
      if(splitWidthHeight <= 0 || splitWidthHeight >= node.width)
        throw new Error(`Split width ${splitWidthHeight} is out of range for node width ${node.width}`)

      // Vertical Split
      node.childNodes[0] = new OptimiserNode(node, {
        id: this.nextNodeId++,
        type: OptimiserSheetNodeTypes.node,
        x: node.x,
        y: node.y,
        width: splitWidthHeight,
        height: node.height
      })
      node.childNodes[1] = new OptimiserNode(node, {
        id: this.nextNodeId++,
        type: OptimiserSheetNodeTypes.node,
        x: node.x + splitWidthHeight,
        y: node.y,
        width: node.width - splitWidthHeight,
        height: node.height
      })
    }
  }


  /**
   * Move a sheet allocation panel into the scratchpad area
   * The scratchpad allows panels to be moved aside to re-arrange the sheet
   */
  moveAllocationToScratchPad(allocation, targetX, targetY, targetWidth, targetHeight, angle) {
    // Clear the node from the tree
    let grabbedAllocNode = this.findNodeById(allocation.packingNodeId)
    if(grabbedAllocNode)
      this._clearNode(grabbedAllocNode, null, false)
    
    allocation.type = SheetAllocationTypes.panelScratchPad
    allocation.x = targetX
    allocation.y = targetY
    allocation.width = targetWidth
    allocation.height = targetHeight
    allocation.angle = angle
    allocation.packingNodeId = null
  }


  /**
   * Split an offcut allocation in half
   */
  splitOffcutAllocation(allocation, splitDirectionType, splitDistance) {
    let offcutNode = this.findNodeById(allocation.packingNodeId)
    if(offcutNode) {
      this._clearNode(offcutNode, null, true)

      this._subdivideNode(offcutNode, splitDirectionType, splitDistance)

      // Insert 2 offcut allocations into the child nodes of this offcut node
      for(let i=0; i<2; i++) {
        let dstNode = offcutNode.childNodes[i]

        // Create a new allocation
        // This is just temporary allocation
        // The server will be updated & return the newly created allocation object that matches the database record
        let newOffcutAlloc = new OptimiserAllocation({
          id: this.nextAllocId++,
          type: SheetAllocationTypes.offcut,
          angle: 0,
          flippedHorz: false,
          flippedVert: false,
          x: dstNode.x,
          y: dstNode.y,
          width: dstNode.width,
          height: dstNode.height,
          sourceWidth: dstNode.width,
          sourceHeight: dstNode.height,
          packingNodeId: dstNode.id,
          isUserLocked: true
        })

        this.optimiserSheetAllocations.push(newOffcutAlloc)

        this._insertAllocationToNode(newOffcutAlloc, false, dstNode, newOffcutAlloc.width, newOffcutAlloc.height, 0, [], false)
      }
    }
  }


  /**
   * Extend a cutting line out to the sheet edges as much as possible
   * Causes nodes to be re-arranged and split differently
   * 
   * If panels could not be re-added after extending the edge, returns false
   */
  extendCuttingLine(node, edgeType) {
    if(!node || !edgeType)
      return false

    // Get neighbouring nodes to the edge
    var lineRect = null
    var lineWidth = 6
    var lineHalfWidth = lineWidth / 2
    var inset = 2

    // Calculate cutting line as a thick rectangle to intersect all nodes
    switch(edgeType) {
      case EdgeTypes.top:
        lineRect = new Rectangle(node.x + inset, node.y - lineHalfWidth, node.width - inset*2, lineWidth)
        break
      case EdgeTypes.right:
        lineRect = new Rectangle(node.x + node.width - lineHalfWidth, node.y + inset, lineWidth, node.height - inset*2)
        break
      case EdgeTypes.bottom:
        lineRect = new Rectangle(node.x + inset, node.y + node.height - lineHalfWidth, node.width - inset*2, lineWidth)
        break
      case EdgeTypes.left:
        lineRect = new Rectangle(node.x - lineHalfWidth, node.y + inset, lineWidth, node.height - inset*2)
        break       
    }


    // Get all leaf nodes in the sheet
    let allNodes = this._flattenNodeDown(this.rootNode).filter(node => !node.hasChildren())

    let nodeList = [node]

    // Cast a fat line to edge & get leaf node intersections with line
    // Stop when we hit a panel / defect
    let intersectingNodes = allNodes.filter(node => node.isRectangleIntersection(lineRect.x, lineRect.y, lineRect.width, lineRect.height))

    // Cannot do anything if intersecting panel/defects
    let blockingNodes = intersectingNodes.filter(node => node.type == OptimiserSheetNodeTypes.panel || node.type == OptimiserSheetNodeTypes.defect)
    if(blockingNodes.length > 0)
      return false

    nodeList = _.concat(nodeList, intersectingNodes)
    nodeList = _.uniqBy(_.compact(nodeList), 'id')

    let commonParent = this._calculateCommonParentForNodeList(nodeList)
    if(!commonParent)
      return

    let parentCopy = commonParent.clone()
    let nodeListCopy = this._flattenNodeDown(parentCopy)
    let removedAllocs = this._clearNode(commonParent, null, true)

    // Just split the parent at the line & attempt to repack the nodes again...
    switch(edgeType) {
      case EdgeTypes.top:
      case EdgeTypes.bottom:
        if(node.x > commonParent.x)
          this._subdivideNode(commonParent, OptimiserNodeSplitDirTypes.vertical, node.x - commonParent.x)
        else if(node.getRight() < commonParent.getRight())
          this._subdivideNode(commonParent, OptimiserNodeSplitDirTypes.vertical, node.getRight() - commonParent.x)
        break

      case EdgeTypes.right:
      case EdgeTypes.left:
        if(node.y > commonParent.y)
          this._subdivideNode(commonParent, OptimiserNodeSplitDirTypes.horizontal, node.y - commonParent.y)
        else if(node.getBottom() < commonParent.getBottom())
          this._subdivideNode(commonParent, OptimiserNodeSplitDirTypes.horizontal, node.getBottom() - commonParent.y)
        break
    }

    // Repack panel & defects, if panel & defects cannot be re-allocated, returns false to undo the operation
    for(let nodeCopy of nodeListCopy) {
      if(nodeCopy.type != OptimiserSheetNodeTypes.panel && nodeCopy.type != OptimiserSheetNodeTypes.defect)
        continue

      let alloc = removedAllocs.find(alloc => alloc.packingNodeId == nodeCopy.id)
      if(alloc) {
        let result = this._insertAllocationToNode(alloc, true, commonParent, alloc.width, alloc.height, alloc.angle, [], null)

        if(result) {
          alloc.isUserLocked = true
          this.optimiserSheetAllocations.push(alloc)
        } else {
          console.error(`   Unable to re-allocate object`)
          return false
        }
      }
    }

    return true
  }


  /**
   * Check if a node edge is extendable outwards
   */
  isEdgeExtendable(node, edgeType) {
    // Must be an offcut or waste
    if(node.type != OptimiserSheetNodeTypes.offcut && node.type != OptimiserSheetNodeTypes.waste)
      return false

    let testRect = null
    let edgeInset = 3
    let testSize = 1

    var offcut = null
    if(node.type == OptimiserSheetNodeTypes.offcut)
      offcut = this.findAllocationAtNodeId(node.id)

    // Check edges
    // 1) If on sheet edge, cannot extend
    // 2) Check if edge neighbours an offcut/waste node only
    // 3) If offcut & offcut has overflow, cannot extend
    switch(edgeType) {
      case EdgeTypes.top:
        if(node.y == 0)
          return false
        if(offcut && offcut.overflowTop > 0)
          return false

        testRect = new Rectangle(node.x + edgeInset, node.y - edgeInset, node.width-edgeInset*2, testSize)
        break

      case EdgeTypes.right:
        if(node.getRight() == this.width)
          return false
        if(offcut && offcut.overflowRight > 0)
          return false

        testRect = new Rectangle(node.x + node.width + edgeInset, node.y + edgeInset, testSize, node.height-edgeInset*2)
        break

      case EdgeTypes.bottom:
        if(node.getBottom() == this.height)
          return false
        if(offcut && offcut.overflowBottom > 0)
          return false

        testRect = new Rectangle(node.x + edgeInset, node.y + node.height + edgeInset, node.width-edgeInset*2, testSize)
        break

      case EdgeTypes.left:
        if(node.x == 0)
          return false
        if(offcut && offcut.overflowLeft > 0)
          return false

        testRect = new Rectangle(node.x - edgeInset, node.y + edgeInset, testSize, node.height-edgeInset*2)
        break
    }

    // Test neighbours
    // If intersects a neighbour that is fixed, cannot extend edge
    if(testRect) {
      let neighbours = this.findNodesIntersectingRectangle(testRect, [OptimiserSheetNodeTypes.panel, OptimiserSheetNodeTypes.defect])
      if(neighbours.length > 0)
        return false
    }

    return true
  }


  /**
   * Add a relief cut at a position for an allocation in the sheet
   * 
   * Returns true if successfully added the relief cut
   */
  addReliefCut(allocation, axisType, point, cuttingLineSegment) {
    // Convert point into allocation pre-transform space
    point = allocation.transformPointToLocal(point)

    // Flip axis cut for allocation rotation
    let isRotated = (allocation.angle == 90 || allocation.angle == 270)
    let allocLeft = -allocation.trimLeft
    let allocTop = -allocation.trimTop
    let allocWidth = allocation.sourceWidth
    let allocHeight = allocation.sourceHeight

    if(isRotated) {
      axisType = (axisType == AxisTypes.horizontal ? AxisTypes.vertical : AxisTypes.horizontal)
    }

    let reliefLine = new GlassSegment(SegmentTypes.line, {
      startPosition: point,
      endPosition: new THREE.Vector2(0, 0)
    })

    let panelPathSegments = allocation.getShapeDataPanelPath()
    if(panelPathSegments.length < 2)
      return false

    // Don't cut through panel itself
    // Convert panel path to polygon
    let panelPointList = MathsUtils.convertSegmentListToPointList(panelPathSegments)
    if(panelPointList.length < 3)
      return false

    // Attempt closest edge first
    // If relief goes through the panel, try furthest edge
    if(axisType == AxisTypes.horizontal) {
      reliefLine.endPosition.y = reliefLine.startPosition.y
      let edge1 = allocLeft, edge2 = allocLeft
      
      if(reliefLine.startPosition.x > allocWidth/2) {
        edge1 = allocWidth
        edge2 = allocLeft
      } else {
        edge1 = allocLeft
        edge2 = allocWidth
      }

      reliefLine.endPosition.x = edge1
      if(MathsUtils.pointIn2DPolygon(reliefLine.getMidpoint(), panelPointList)) {
        reliefLine.endPosition.x = edge2
      }
    } else {
      reliefLine.endPosition.x = reliefLine.startPosition.x
      let edge1 = allocTop, edge2 = allocTop
      
      if(reliefLine.startPosition.y > allocHeight/2) {
        edge1 = allocHeight
        edge2 = allocTop
      } else {
        edge1 = allocTop
        edge2 = allocHeight
      }

      reliefLine.endPosition.y = edge1
      if(MathsUtils.pointIn2DPolygon(reliefLine.getMidpoint(), panelPointList)) {
        reliefLine.endPosition.y = edge2
      }
    }

    allocation.addReliefCut(reliefLine.startPosition, reliefLine.endPosition)

    return true
  }


  /**
   * Remove a relief cut from an allocation
   */
  removeReliefCut(allocation, reliefCutIndex) {
    if(reliefCutIndex < 0 || reliefCutIndex >= allocation.reliefCutList.length)
      return false

    allocation.reliefCutList.splice(reliefCutIndex, 1)

    return true
  }


  /**
   * Remove all relief cuts from an allocation
   */
  removeAllReliefCuts(allocation) {
    allocation.removeAllReliefCuts()

    return true
  }


  /**
   * Add a cutout to an allocation to be cut at optimisation
   * The new cutout may require new trim data. Will need to resize the allocation to fit the new trim
   * 
   * Returns true if the allocation could resize correct with the cutout update. Otherwise returns false
   */
  addCutout(allocation, cutoutIndex, requiredTrimData) {
    var allocOriginalRect = allocation.getRectangle()

    allocation.updateTrimData(requiredTrimData.trimTop, requiredTrimData.trimRight, requiredTrimData.trimBottom, requiredTrimData.trimLeft)
    allocation.addCutout(cutoutIndex)

    // Resize into node
    let targetWidth = allocation.width
    let targetHeight = allocation.height
    let targetLeft = allocation.x
    let targetTop = allocation.y

    // Recalculate nodes for the target rectangle
    let position = { x: targetLeft + targetWidth/2, y: targetTop + targetHeight/2 }
    let resizeOptions = { nodeResized: true, oldAllocRect: allocOriginalRect }
    if(!this.resizeNodePositionInTree(allocation, allocation.packingNodeId, targetLeft, targetTop, targetWidth, targetHeight, allocation.angle, position, resizeOptions)) {
      return false
    }

    return true
  }


  /**
   * Remove a cutout from the allocation so it's no longer cut at optimisation
   * Removing a cutout may require new trim data. Will need to resize the allocation to fit the updated trim
   * 
   * Returns true if the allocation could resize correct with the cutout update. Otherwise returns false
   */
  removeCutout(allocation, cutoutIndex, requiredTrimData) {
    var allocOriginalRect = allocation.getRectangle()

    allocation.updateTrimData(requiredTrimData.trimTop, requiredTrimData.trimRight, requiredTrimData.trimBottom, requiredTrimData.trimLeft)
    allocation.removeCutout(cutoutIndex)

    // Resize into node
    let targetWidth = allocation.width
    let targetHeight = allocation.height
    let targetLeft = allocation.x
    let targetTop = allocation.y

    // Recalculate nodes for the target rectangle
    let position = { x: targetLeft + targetWidth/2, y: targetTop + targetHeight/2 }
    let resizeOptions = { nodeResized: true, oldAllocRect: allocOriginalRect }
    if(!this.resizeNodePositionInTree(allocation, allocation.packingNodeId, targetLeft, targetTop, targetWidth, targetHeight, allocation.angle, position, resizeOptions)) {
      return false
    }

    return true
  }


  /**
   * Remove all cutouts from the allocation so it's no longer cut at optimisation
   */
  removeAllCutouts(allocation) {
    allocation.removeAllCutouts()
  }


  /**
   * Find a sheet allocation object that contains a point in screen coordinates
   * 
   */
  getSheetAllocationAtPosition(x, y) {
    return this.optimiserSheetAllocations.find(alloc => {
      return alloc.isPointInside(x, y)
    })
  }


  /**
   * Get all sheet allocations that intersect with a rectangle
   * Options include:
   *    ignoreAllocId - If set, ignores allocs matching the id
   *    types - If set, filters by those types
   */
  findSheetAllocationsIntersectingRectangle(rect, options = {}) {
    return this.optimiserSheetAllocations
    .filter(alloc => {
      return rect.intersectsBox(alloc.getRectangle())
    })
    .filter(alloc => {
      if(options.ignoreAllocId && alloc.id == options.ignoreAllocId)
        return false
      if(options.types && !options.types.includes(alloc.type))
        return false
      
      return true
    })
  }


  /**
   * Find a sheet allocation object given an id
   * Returns null if not found
   */
  findAllocationById(id) {
    return this.optimiserSheetAllocations.find(alloc => {
      return alloc.id == id
    })
  }


  /**
   * Sort a list of allocations by a direction. From closest to furthest
   * Returns sorted list
   */
  sortAllocationsInDirection(allocationList, sortDir) {
    var sortedList = [...allocationList]
    if(sortedList.length <= 1)
      return sortedList

    var offsetPoint = sortedList[0].getRectangle().getCenter()

    sortedList.sort((alloc1, alloc2) => {
      var dist1 = MathsUtils.calculateDistanceToPlane2D(sortDir, offsetPoint, alloc1.getRectangle().getCenter())
      var dist2 = MathsUtils.calculateDistanceToPlane2D(sortDir, offsetPoint, alloc2.getRectangle().getCenter())

      return dist1 - dist2
    })

    return sortedList
  }



  /**
   * Find a sheet allocation object for a node in the tree
   * Returns null if not found
   */
  findAllocationAtNodeId(nodeId) {
    return this.optimiserSheetAllocations.find(alloc => {
      return alloc.packingNodeId == nodeId
    })
  }


  /**
   * Find a node given an allocation
   * Returns null if not found
   */
  findNodeAtAllocationId(allocationId) {
    let alloc = this.findAllocationById(allocationId)
    if(!alloc)
      return null

    return this.findNodeById(alloc.packingNodeId)
  }


  /**
   * Find the lowest node that contains a position
   * Returns null if not found
   */
  findNodeAtPosition(x, y, node = null) {
    if(!node)
      node = this.rootNode

    let containsPoint = node.nodeContainsPoint(x, y)
    if(!containsPoint)
      return null

    // Children first
    if(node.childNodes[0]) {
      let foundNode = this.findNodeAtPosition(x, y, node.childNodes[0])
      if(foundNode)
        return foundNode
    }
    if(node.childNodes[1]) {
      let foundNode = this.findNodeAtPosition(x, y, node.childNodes[1])
      if(foundNode)
        return foundNode
    }

    return node
  }


  /**
   * Get all nodes that intersect with a rectangle
   * Filters by node types if specified
   * 
   * Returns a list of all nodes intersecting (or containing) the rectangle
   */
  findNodesIntersectingRectangle(rect, nodeTypes = null, node = null) {
    let result = []

    if(!node)
      node = this.rootNode

    // If a leaf node & not in types, ignore it
    // Have to always continue for branch nodes
    if(nodeTypes && node.type != OptimiserSheetNodeTypes.node) {
      if(nodeTypes.indexOf(node.type) == -1)
        return []
    }

    // If doesn't intersect node, ignore children
    let intersection = node.isRectangleIntersection(rect.x, rect.y, rect.width, rect.height)
    if(!intersection)
      return []

    if(node.childNodes[0])
      result = _.concat(result, this.findNodesIntersectingRectangle(rect, nodeTypes, node.childNodes[0]))
    if(node.childNodes[1])
      result = _.concat(result, this.findNodesIntersectingRectangle(rect, nodeTypes, node.childNodes[1]))

    if(!nodeTypes || nodeTypes.indexOf(node.type) != -1)
      result.push(node)

    return result
  }


  /**
   * Find a node edge that is nearest a position
   * Returns info about the edge (including node & side)
   * Returns null if the point is greater than the minimum distance
   */
  findNearestNodeEdgeAtPosition(node, x, y, minDistance = null) {
    let top = Math.abs(y - node.y)
    let bottom = Math.abs(y - (node.y + node.height))
    let left = Math.abs(x - node.x)
    let right = Math.abs(x - (node.x + node.width))

    if(top < bottom) {
      if(top < left && top < right) {
        if(minDistance && top > minDistance)
          return null

        return EdgeTypes.top
      }
    }
    else {
      if(bottom < left && bottom < right) {
        if(minDistance && bottom > minDistance)
          return null
        return EdgeTypes.bottom
      }
    }

    if(left < right) {
      if(minDistance && left > minDistance)
        return null

      return EdgeTypes.left
    } else {
      if(minDistance && right > minDistance)
        return null

      return EdgeTypes.right
    }
  }


  /**
   * Find a node that contains a panel ID
   * Returns null if not found
   */
  findNodeByPanelId(panelId, node = null) {
    if(!node)
      node = this.rootNode

    if(node.type == OptimiserSheetNodeTypes.panel) {
      if(node.panelId == panelId)
        return node
    }

    if(node.childNodes[0]) {
      let foundNode = this.findNodeByPanelId(panelId, node.childNodes[0])
      if(foundNode)
        return foundNode
    }
    if(node.childNodes[1]) {
      let foundNode = this.findNodeByPanelId(panelId, node.childNodes[1])
      if(foundNode)
        return foundNode
    }

    return null
  }


  /**
   * Debug output to console the node tree
   */
  debugOutputNodeTree() {
    console.log('%cNode Tree:', 'font-weight:bold;font-size:14px')
    console.log(this._debugGenerateNodeOutputData(this.rootNode))
  }


  /**
   * Output a node recursively for debugging
   */
  _debugGenerateNodeOutputData(node, level = 0) {
    if(!node)
      return ''

    let output = (' '.repeat(level * 2)) + node.toString()
    if(node.childNodes[0])
      output += '\n'

    output += this._debugGenerateNodeOutputData(node.childNodes[0], level+1)
    output += this._debugGenerateNodeOutputData(node.childNodes[1], level+1)

    return output
  }


  /**
   * Get the next allocation id available
   */
  getNextAllocationId() {
    var lastId = _.max(this.optimiserSheetAllocations.map(alloc => alloc.id || 0)) || 0
    return lastId + 1
  }




}